/**
 * Cosmetic overlay whitelist + sanitiser — the per-user PRESENTATION-ONLY
 * override contract for the WindowSpec renderer (Entity OS Phase-1, roadmap #429).
 *
 * SINGLE SOURCE OF TRUTH (PR-c/3):
 *   This module is the canonical home of the cosmetic prop whitelist. Both ends
 *   import it so the security boundary can never drift between client and server:
 *     - the web renderer (WindowSpecRenderer / applyCosmeticOverlay) merges only
 *       whitelisted props onto each node at render time, and
 *     - the API persist layer (uiOverlay.service.saveOverlay) re-validates the
 *       payload server-side BEFORE writing, never trusting the client.
 *
 * SECURITY BOUNDARY:
 *   A user overlay may ONLY carry presentation props on the WHITELIST below. It
 *   can NEVER touch data binding or behaviour — `dataSource`, `tool`, `action`,
 *   `onChange`, `onClick`, `value`, `type`, `children`, prototype-pollution keys
 *   (`__proto__`, `constructor`, `prototype`), case-variants of whitelisted keys,
 *   or anything else are dropped. The whitelist match is exact and case-sensitive.
 *
 * TIGHTENING-only (gh#1228) — VALUE TYPE CONTRACT:
 *   Beyond the key whitelist, each whitelisted prop now also has a VALUE type
 *   contract (label/title/placeholder → string, order → finite number, width →
 *   finite number OR string, hidden → boolean). A whitelisted key carrying a
 *   WRONG-typed value is DROPPED — the SAME drop-not-reject semantic as a
 *   non-whitelisted key, so sanitisation stays idempotent and self-healing (a
 *   re-save of a legacy row silently sheds an invalid-typed value; nothing throws).
 *   This is a pure NARROWING of what survives — the key whitelist is UNCHANGED (the
 *   6 props are fixed) and no previously-valid payload changes. The read-side merge
 *   (`applyCosmeticOverlay` in the web renderer) is DELIBERATELY left tolerant
 *   (key-filter only, no value check): a legacy row with an odd-typed value keeps
 *   rendering until the next write heals it, and the per-primitive renderer schema
 *   is the final render-time guard. The persist gate here is the authoritative
 *   narrowing point.
 *
 * Shape: the overlay is a `Record<nodeId, Record<prop, value>>`. Only OWN,
 * whitelisted keys with a valid-typed value of each node entry survive sanitisation.
 *
 * These functions are deliberately tiny, pure and side-effect-free so the
 * contract is auditable in one place and unit-testable in isolation.
 */
/**
 * Whitelist of cosmetic, presentation-only props a user overlay may override.
 * STRICT (user decision): nothing here influences data, behaviour, or wiring.
 *
 * Extending this list is a SECURITY decision — only add props that are purely
 * visual/layout and can never change what data is fetched or what action fires.
 */
export const COSMETIC_PROP_WHITELIST = Object.freeze([
    'label',
    'title',
    'order',
    'width',
    'hidden',
    'placeholder',
]);
const WHITELIST_SET = new Set(COSMETIC_PROP_WHITELIST);
/** True when `prop` is an allowed cosmetic override key (exact, case-sensitive). */
export function isCosmeticProp(prop) {
    return WHITELIST_SET.has(prop);
}
/**
 * Per-COLUMN cosmetic whitelist (gh#1261) — the SUBSET of `COSMETIC_PROP_WHITELIST`
 * that is meaningful on a list/board COLUMN entry (`props.columns[i]`) AND is actually
 * honoured by the column reader (`applyViewOverlay` / `mergeColumn`):
 *   - `label`  → column header override (merged)
 *   - `width`  → column width override (merged)
 *   - `hidden` → drop the column (filter)
 * The three OTHER whitelisted props are DELIBERATELY excluded for columns:
 *   - `order`               → column position is expressed by ARRAY POSITION, so a
 *                             re-order is a STRUCTURAL column change (rejected), never a
 *                             per-column `order` prop; and `applyViewOverlay` would
 *                             re-sort on it — leaving it out keeps the reader honest.
 *   - `title` / `placeholder` → column NO-OPs in `applyViewOverlay` (they apply to the
 *                             ROOT node / form inputs), so admitting them would persist
 *                             an entry the reader ignores and break round-trip parity.
 *
 * This is the column-decompose boundary the `specDiff` column path (specDiff.ts) uses
 * to reduce a WHOLESALE `props.columns` array set into per-column pseudo-id overlay
 * entries. It stays a SUBSET of the master whitelist, so `sanitizeOverlay` (which keys
 * off the full set) always passes a decomposed column entry through unchanged.
 */
export const COLUMN_COSMETIC_PROP_WHITELIST = Object.freeze([
    'label',
    'width',
    'hidden',
]);
const COLUMN_WHITELIST_SET = new Set(COLUMN_COSMETIC_PROP_WHITELIST);
/** True when `prop` is an allowed per-column cosmetic override key (exact, case-sensitive). */
export function isColumnCosmeticProp(prop) {
    return COLUMN_WHITELIST_SET.has(prop);
}
/**
 * Per-prop VALUE type contract (gh#1228 — TIGHTENING-only). Keyed by `CosmeticProp`
 * so this map is EXHAUSTIVE at compile time: a whitelist prop with no validator here
 * is a type error, and the 6 props are fixed (adding/removing one is a separate
 * SECURITY decision). A whitelisted key whose value FAILS its validator is dropped by
 * `sanitizeNodeEntry` (drop-not-reject — same as an unknown key). `Number.isFinite`
 * rejects NaN/±Infinity so a numeric prop can never persist a non-finite value.
 *   - label / title / placeholder → string
 *   - order                       → finite number
 *   - width                       → finite number OR string (px number or CSS length)
 *   - hidden                      → boolean
 */
const COSMETIC_VALUE_VALIDATORS = Object.freeze({
    label: (v) => typeof v === 'string',
    title: (v) => typeof v === 'string',
    placeholder: (v) => typeof v === 'string',
    order: (v) => typeof v === 'number' && Number.isFinite(v),
    width: (v) => typeof v === 'string' || (typeof v === 'number' && Number.isFinite(v)),
    hidden: (v) => typeof v === 'boolean',
});
/**
 * Pick the OWN, whitelisted keys — WITH A VALID-TYPED VALUE — of a single node's
 * override bag.
 *
 * Returns a NEW plain object (null-prototype-safe: built from `{}` with explicit
 * own-key copy). Inherited keys and prototype-pollution keys are never copied
 * because (a) they are not on the whitelist and (b) the iteration is over
 * `Object.keys` (own enumerable) guarded by `hasOwnProperty`.
 *
 * gh#1228 TIGHTENING: a whitelisted key whose value fails its `COSMETIC_VALUE_VALIDATORS`
 * type contract is DROPPED (not rejected) — the same drop semantic as an unknown key,
 * so a forged/corrupt payload can never persist e.g. `hidden: "yes"` or `order: {}`.
 * The `WHITELIST_SET.has(key)` guard runs first, so the validator is only ever indexed
 * with one of the 6 fixed cosmetic props (never a prototype key).
 */
function sanitizeNodeEntry(entry) {
    const clean = {};
    if (!entry || typeof entry !== 'object')
        return clean;
    for (const key of Object.keys(entry)) {
        if (!WHITELIST_SET.has(key))
            continue; // drop non-cosmetic / unknown / unsafe / case-variant
        if (!Object.prototype.hasOwnProperty.call(entry, key))
            continue;
        const value = entry[key];
        if (!COSMETIC_VALUE_VALIDATORS[key](value))
            continue; // drop wrong-typed value
        clean[key] = value;
    }
    return clean;
}
/**
 * Server-side enforcement core: return a SANITISED copy of an overlay payload
 * containing ONLY whitelisted cosmetic props per node.
 *
 * - Input is untrusted (e.g. a raw request body). Non-object input yields `{}`.
 * - Each node id maps to a fresh object with only whitelisted own keys.
 * - A node whose entry has no whitelisted keys is DROPPED entirely (no empty
 *   bag persisted), keeping the stored payload minimal.
 * - Never mutates the input; never copies inherited / prototype-pollution keys;
 *   node-id keys themselves are filtered with `hasOwnProperty` so a malicious
 *   `__proto__` top-level key cannot smuggle an entry through.
 *
 * This is the authoritative gate the persist layer applies BEFORE writing, so a
 * forged client payload (bypassing the renderer) can never store binding props.
 */
export function sanitizeOverlay(input) {
    const out = {};
    if (!input || typeof input !== 'object')
        return out;
    for (const nodeId of Object.keys(input)) {
        if (!Object.prototype.hasOwnProperty.call(input, nodeId))
            continue;
        const cleaned = sanitizeNodeEntry(input[nodeId]);
        // Drop nodes that contributed no whitelisted prop — keep the payload minimal.
        if (Object.keys(cleaned).length > 0) {
            out[nodeId] = cleaned;
        }
    }
    return out;
}
//# sourceMappingURL=cosmeticOverlay.js.map